import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Sparkle,
  CheckCircle,
  WarningCircle,
  ClockCounterClockwise,
  Spinner,
  ArrowClockwise,
  ShieldCheck,
  Check,
  Warning,
  Database,
  ArrowRight,
} from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import {
  donorPredictionService,
  type TrainingPrecheckResponse,
  type TrainingStatusResponse,
  type ModelVersionSummary,
} from '../../../services/donorPrediction.service';
import { CalendarDatePicker, formatDisplayDate } from './CalendarDatePicker';
import { toast } from 'sonner';

interface TrainModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeModelVersion: string;
  onActiveModelChanged: (version: string) => void;
}

export const TrainModelModal: React.FC<TrainModelModalProps> = ({
  isOpen,
  onClose,
  activeModelVersion,
  onActiveModelChanged,
}) => {
  const [activeTab, setActiveTab] = useState<'train' | 'registry'>('train');

  // Form Inputs
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // Precheck state (Loaded dynamically from backend MongoDB discovery)
  const [precheckLoading, setPrecheckLoading] = useState<boolean>(false);
  const [precheckData, setPrecheckData] = useState<TrainingPrecheckResponse | null>(null);
  const [precheckError, setPrecheckError] = useState<string | null>(null);

  // Background Job State
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<TrainingStatusResponse | null>(null);
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const pollTimerRef = useRef<any>(null);
  const pollStartTimeRef = useRef<number>(0);

  // Model Registry State
  const [models, setModels] = useState<ModelVersionSummary[]>([]);
  const [loadingModels, setLoadingModels] = useState<boolean>(false);

  // Activation Confirmation Dialog State
  const [confirmActivateVersion, setConfirmActivateVersion] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Dynamic bounds calculation
  const todayStr = new Date().toISOString().split('T')[0];
  const earliestDbDate = precheckData?.database_bounds?.earliest_project_date || '2005-01-29';
  const latestDbDate = precheckData?.database_bounds?.latest_project_date || '2026-09-18';
  // Maximum selectable End Date = min(today, latest_database_project_date)
  const maxEndDate = latestDbDate < todayStr ? latestDbDate : todayStr;
  const minStartDate = earliestDbDate;

  // Fetch registered models and database precheck on mount / open
  useEffect(() => {
    if (isOpen) {
      loadRegisteredModels();
      performPrecheck();
    }
  }, [isOpen]);

  // Clean up polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const loadRegisteredModels = async () => {
    setLoadingModels(true);
    try {
      const res = await donorPredictionService.listModels();
      setModels(res.models || []);
      if (res.active_version) {
        onActiveModelChanged(res.active_version);
      }
    } catch (err: any) {
      toast.error('Unable to retrieve model registry. Please check backend connection.');
    } finally {
      setLoadingModels(false);
    }
  };

  const performPrecheck = async (fDate?: string, tDate?: string) => {
    setPrecheckLoading(true);
    setPrecheckError(null);
    try {
      const reqFrom = fDate !== undefined ? fDate : (startDate || undefined);
      const reqTo = tDate !== undefined ? tDate : (endDate || undefined);

      const res = await donorPredictionService.precheckTraining({
        from_date: reqFrom,
        to_date: reqTo,
      });
      setPrecheckData(res);

      // Auto-populate default dates on first load if not set
      if (!startDate && res.from_date) {
        setStartDate(res.from_date);
      }
      if (!endDate && res.to_date) {
        setEndDate(res.to_date);
      }
    } catch (err: any) {
      setPrecheckData(null);
      setPrecheckError('Unable to connect to database for historical range discovery.');
    } finally {
      setPrecheckLoading(false);
    }
  };

  const handleStartDateChange = (newStartDate: string) => {
    setStartDate(newStartDate);
    let nextEndDate = endDate;
    if (endDate && endDate < newStartDate) {
      nextEndDate = newStartDate;
      setEndDate(newStartDate);
      toast.info('End Date adjusted to match valid Start Date window.');
    }
    performPrecheck(newStartDate, nextEndDate);
  };

  const handleEndDateChange = (newEndDate: string) => {
    if (newEndDate > maxEndDate) {
      toast.error('End Date cannot exceed maximum historical date or future date.');
      return;
    }
    setEndDate(newEndDate);
    performPrecheck(startDate, newEndDate);
  };

  const startTrainingJob = async () => {
    if (precheckData && !precheckData.can_train) {
      toast.error(precheckData.warning || 'Historical dataset is insufficient for training.');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select both a Start Date and an End Date.');
      return;
    }

    setIsTraining(true);
    setJobStatus(null);
    pollStartTimeRef.current = Date.now();

    try {
      const res = await donorPredictionService.startTraining({
        from_date: startDate,
        to_date: endDate,
        description: description.trim() || undefined,
      });

      setActiveJobId(res.job_id);
      toast.success('Warm-start candidate training dispatched to background worker.');

      // Begin status polling
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(async () => {
        try {
          const statusRes = await donorPredictionService.getTrainingStatus(res.job_id);
          setJobStatus(statusRes);

          // Polling timeout protection (3 minutes)
          if (Date.now() - pollStartTimeRef.current > 180000) {
            clearInterval(pollTimerRef.current);
            setIsTraining(false);
            toast.error('Training status polling timed out. Please check model registry.');
            return;
          }

          if (statusRes.status === 'completed') {
            clearInterval(pollTimerRef.current);
            setIsTraining(false);
            toast.success(`Candidate ${statusRes.version_id} created successfully.`);
            await loadRegisteredModels();
          } else if (statusRes.status === 'failed') {
            clearInterval(pollTimerRef.current);
            setIsTraining(false);
            toast.error(statusRes.error || 'Training process encountered an error.');
          }
        } catch (pollErr) {
          console.error('[TrainModelModal] Polling error:', pollErr);
        }
      }, 1500);

    } catch (err: any) {
      setIsTraining(false);
      toast.error(err.message || 'Failed to initiate training job');
    }
  };

  const handleConfirmActivate = async () => {
    if (!confirmActivateVersion) return;
    setIsActivating(true);
    try {
      const res = await donorPredictionService.activateModel(confirmActivateVersion);
      onActiveModelChanged(res.active_version);
      toast.success(`Model ${res.active_version} is now active in production.`);
      await loadRegisteredModels();
      setConfirmActivateVersion(null);
      // Cleanly reset evaluation screen if the activated model was the current job
      if (jobStatus?.version_id === confirmActivateVersion) {
        setJobStatus(null);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate model version');
    } finally {
      setIsActivating(false);
    }
  };

  const handleKeepCurrentModel = () => {
    // Keep current champion active, reset completed job view, candidate remains registered
    toast.info(`Active champion model remains ${activeModelVersion}. Candidate is safely stored in registry.`);
    setJobStatus(null);
  };

  // Helper formatting for neutral metrics & delta
  const formatMetric = (val: number | undefined | null, isPercent = false): string => {
    if (val === undefined || val === null || isNaN(val)) return '—';
    if (isPercent) {
      return `${(val * 100).toFixed(2)}%`;
    }
    return Number(val).toFixed(4);
  };

  const formatDelta = (delta: number | undefined | null, isPercent = false): string => {
    if (delta === undefined || delta === null || isNaN(delta)) return '—';
    const prefix = delta > 0 ? '+' : '';
    if (isPercent) {
      return `Δ ${prefix}${(delta * 100).toFixed(2)}%`;
    }
    return `Δ ${prefix}${Number(delta).toFixed(4)}`;
  };

  if (!isOpen) return null;

  // Extract resolved candidate, champion and delta metrics
  const candMetrics = jobStatus?.metrics?.candidate;
  const champMetrics = jobStatus?.metrics?.champion;
  const deltaMetrics = jobStatus?.metrics?.delta_vs_champion;

  // Calculate hit_10 delta if not explicitly in delta object
  const calcHitDelta = (candHit?: number, champHit?: number): number | null => {
    if (candHit !== undefined && champHit !== undefined) {
      return candHit - champHit;
    }
    return null;
  };

  const headAHitDelta = calcHitDelta(candMetrics?.head_a?.hit_10, champMetrics?.head_a?.hit_10);
  const headBHitDelta = calcHitDelta(candMetrics?.head_b?.hit_10, champMetrics?.head_b?.hit_10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200/80 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden font-sans">
        
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-accent-light text-accent border border-accent/20 flex items-center justify-center">
              <Sparkle size={20} weight="bold" />
            </div>
            <div>
              <h2 className="font-outfit font-extrabold text-xl text-slate-900 leading-tight">
                Train Recommendation Model
              </h2>
              <p className="text-[11px] font-semibold text-slate-400">
                Champion / Challenger Point-in-Time LightGBM Warm-Start Training
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-100 px-6 bg-slate-50/30">
          <button
            onClick={() => setActiveTab('train')}
            className={`py-2.5 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'train'
                ? 'border-accent text-accent'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            <Sparkle size={14} weight="bold" />
            Champion / Challenger Training
          </button>
          <button
            onClick={() => setActiveTab('registry')}
            className={`py-2.5 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'registry'
                ? 'border-accent text-accent'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            <ClockCounterClockwise size={14} weight="bold" />
            Model Registry
            <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200/80 text-slate-600 font-extrabold">
              {models.length}
            </span>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {activeTab === 'train' ? (
            <>
              {/* STEP 1: READY STATE (Not actively training, no unreviewed completed candidate) */}
              {!isTraining && !jobStatus?.version_id && (
                <div className="space-y-4">
                  {/* Current Active Champion Card */}
                  <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center justify-between">
                    <div className="space-y-1">
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                        Current Champion Model
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-outfit font-extrabold text-base text-slate-900 font-mono">
                          {activeModelVersion}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          ACTIVE / CHAMPION
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium">
                        Production model serving live donor recommendations
                      </p>
                    </div>
                  </div>

                  {/* Training Data & Date Range Card */}
                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Database size={13} weight="bold" className="text-blue-500" />
                        Training Data
                      </span>
                      <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-md">
                        Source: MongoDB
                      </span>
                    </div>

                    {/* Available Historical Range */}
                    <div className="bg-white p-3 rounded-xl border border-slate-200/60 flex items-center justify-between text-xs">
                      <span className="text-[11px] font-bold text-slate-500">
                        Available Historical Range:
                      </span>
                      <span className="font-mono font-bold text-slate-900">
                        {formatDisplayDate(earliestDbDate)} → {formatDisplayDate(latestDbDate)}
                      </span>
                    </div>

                    {/* Training Period with Calendar Date Pickers */}
                    <div className="space-y-1.5 pt-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">
                          Training Period
                        </span>
                        {precheckLoading && (
                          <span className="text-[10px] font-bold text-accent flex items-center gap-1">
                            <Spinner size={11} className="animate-spin" /> Updating counts...
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <CalendarDatePicker
                          label="Start Date"
                          value={startDate}
                          onChange={handleStartDateChange}
                          minDate={minStartDate}
                          maxDate={maxEndDate}
                          placeholder="DD/MM/YYYY"
                        />
                        <CalendarDatePicker
                          label="End Date"
                          value={endDate}
                          onChange={handleEndDateChange}
                          minDate={startDate || minStartDate}
                          maxDate={maxEndDate}
                          placeholder="DD/MM/YYYY"
                        />
                      </div>
                    </div>

                    {/* Selected Range Live Counts */}
                    {precheckError ? (
                      <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-[11px] font-bold text-amber-700 flex items-center gap-2">
                        <WarningCircle size={15} weight="bold" />
                        {precheckError}
                      </div>
                    ) : precheckData ? (
                      <div className="space-y-2.5">
                        <div className="grid grid-cols-2 gap-2.5">
                          <div className="bg-white p-2.5 rounded-xl border border-slate-200/60 text-center">
                            <span className="block text-[9px] font-extrabold text-slate-400 uppercase">
                              Projects in selected range
                            </span>
                            <span className="font-outfit font-extrabold text-lg text-slate-900">
                              {precheckData.projects}
                            </span>
                          </div>
                          <div className="bg-white p-2.5 rounded-xl border border-slate-200/60 text-center">
                            <span className="block text-[9px] font-extrabold text-slate-400 uppercase">
                              Donation records
                            </span>
                            <span className="font-outfit font-extrabold text-lg text-slate-900">
                              {precheckData.donations.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Point-in-time / leakage safe note */}
                        <div className="bg-white p-2.5 rounded-xl border border-slate-200/60 flex items-center justify-between text-xs">
                          <span className="text-[11px] font-bold text-slate-500">
                            Training preparation:
                          </span>
                          <span className="font-semibold text-blue-700 flex items-center gap-1">
                            <CheckCircle size={12} weight="bold" className="text-blue-500" />
                            Point-in-time / leakage-safe
                          </span>
                        </div>

                        {/* Warning if cannot train */}
                        {!precheckData.can_train && precheckData.warning && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-[11px] font-bold text-amber-800 flex items-start gap-2">
                            <WarningCircle size={16} weight="bold" className="shrink-0 text-amber-600 mt-0.5" />
                            <span>{precheckData.warning}</span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Training Strategy Card */}
                  <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-extrabold text-indigo-700 uppercase tracking-wider">
                        Training Strategy
                      </span>
                    </div>
                    <div className="font-outfit font-extrabold text-sm text-indigo-950 flex items-center gap-2">
                      <span>Champion</span>
                      <ArrowRight size={13} weight="bold" className="text-indigo-400" />
                      <span>Warm-Start Candidate</span>
                    </div>
                    <p className="text-[11px] text-indigo-900/80 leading-relaxed font-medium">
                      Continues training from the current champion model using LightGBM warm-starting. The current champion is preserved.
                    </p>
                  </div>

                  {/* Optional Run Description */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">
                      Description / Run Tag (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Q3 Warm-start candidate run"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
                    />
                  </div>
                </div>
              )}

              {/* STEP 2: IN-FLIGHT TRAINING STATE */}
              {isTraining && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Spinner size={18} className="animate-spin text-accent" />
                      <h4 className="font-outfit font-extrabold text-sm text-slate-900">
                        Training Candidate in Background...
                      </h4>
                    </div>
                    {jobStatus?.elapsed_seconds !== undefined && (
                      <span className="text-[10px] font-extrabold font-mono text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-md">
                        {jobStatus.elapsed_seconds}s
                      </span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-accent h-full transition-all duration-300 rounded-full"
                      style={{ width: `${jobStatus?.progress_pct || 10}%` }}
                    />
                  </div>

                  <p className="text-xs font-bold text-slate-700">
                    {jobStatus?.stage || 'Preparing training data from MongoDB...'}
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-[10px] font-semibold text-slate-500 pt-1">
                    <div>Data Source: MongoDB point-in-time</div>
                    <div>Strategy: Warm-start from {activeModelVersion}</div>
                    <div className="truncate font-mono col-span-2">Job ID: {activeJobId || '...'}</div>
                  </div>
                </div>
              )}

              {/* STEP 3: COMPARATIVE EVALUATION STATE (Training Completed) */}
              {!isTraining && jobStatus?.status === 'completed' && jobStatus?.version_id && (
                <div className="space-y-4">
                  {/* Status Banner */}
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={18} weight="bold" className="text-emerald-600" />
                        <h4 className="font-outfit font-extrabold text-base text-slate-900">
                          Training Complete
                        </h4>
                      </div>
                      <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                        CANDIDATE — NOT ACTIVE
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs pt-1">
                      <div className="bg-white p-2.5 rounded-xl border border-slate-200/60">
                        <span className="block text-[9px] font-extrabold text-slate-400 uppercase">Current Champion</span>
                        <span className="font-mono font-bold text-slate-800">{activeModelVersion}</span>
                      </div>
                      <div className="bg-white p-2.5 rounded-xl border border-slate-200/60">
                        <span className="block text-[9px] font-extrabold text-slate-400 uppercase">Candidate Model</span>
                        <span className="font-mono font-bold text-slate-800">{jobStatus.version_id}</span>
                      </div>
                    </div>

                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                      Holdout validation metrics computed. The candidate remains inactive until you explicitly choose to activate it.
                    </p>
                  </div>

                  {/* Comparative Metrics: Head A — Existing Donors */}
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-extrabold text-slate-800">
                        Head A — Existing Donors
                      </span>
                      <span className="text-[10px] font-semibold text-slate-400">
                        Holdout Validation
                      </span>
                    </div>
                    <table className="w-full text-left text-xs font-mono">
                      <thead>
                        <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase">
                          <th className="py-2 px-4 font-sans">Metric</th>
                          <th className="py-2 px-3 text-right">Champion</th>
                          <th className="py-2 px-3 text-right">Candidate</th>
                          <th className="py-2 px-4 text-right">Δ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">NDCG@10</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_a?.ndcg_10)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_a?.ndcg_10)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_a?.ndcg_10)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">NDCG@50</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_a?.ndcg_50)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_a?.ndcg_50)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_a?.ndcg_50)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">Hit@10</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_a?.hit_10, true)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_a?.hit_10, true)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(headAHitDelta, true)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">MRR</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_a?.mrr)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_a?.mrr)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_a?.mrr)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Comparative Metrics: Head B — New Prospects */}
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-extrabold text-slate-800">
                        Head B — New Prospects
                      </span>
                      <span className="text-[10px] font-semibold text-slate-400">
                        Holdout Validation
                      </span>
                    </div>
                    <table className="w-full text-left text-xs font-mono">
                      <thead>
                        <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase">
                          <th className="py-2 px-4 font-sans">Metric</th>
                          <th className="py-2 px-3 text-right">Champion</th>
                          <th className="py-2 px-3 text-right">Candidate</th>
                          <th className="py-2 px-4 text-right">Δ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">NDCG@10</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_b?.ndcg_10)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_b?.ndcg_10)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_b?.ndcg_10)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">NDCG@50</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_b?.ndcg_50)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_b?.ndcg_50)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_b?.ndcg_50)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">Hit@10</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_b?.hit_10, true)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_b?.hit_10, true)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(headBHitDelta, true)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4 font-sans font-semibold text-slate-800">MRR</td>
                          <td className="py-2 px-3 text-right">{formatMetric(champMetrics?.head_b?.mrr)}</td>
                          <td className="py-2 px-3 text-right">{formatMetric(candMetrics?.head_b?.mrr)}</td>
                          <td className="py-2 px-4 text-right font-bold text-slate-700">
                            {formatDelta(deltaMetrics?.head_b?.mrr)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Decision Actions */}
                  <div className="space-y-2 pt-1">
                    <div className="flex gap-2.5">
                      <Button
                        onClick={handleKeepCurrentModel}
                        variant="outline"
                        className="flex-1 text-xs py-2 font-bold"
                      >
                        Keep Current Model
                      </Button>
                      <Button
                        onClick={() => setConfirmActivateVersion(jobStatus.version_id!)}
                        className="flex-1 bg-accent hover:bg-accent-hover text-white text-xs py-2 font-bold"
                      >
                        <Check size={14} weight="bold" />
                        Activate Candidate
                      </Button>
                    </div>
                    <p className="text-[10px] text-center text-slate-400 font-semibold">
                      "Keep Current Model" preserves {activeModelVersion}. The candidate is safely saved in the registry.
                    </p>
                  </div>
                </div>
              )}

              {/* STEP: ERROR STATE IF TRAINING FAILED */}
              {!isTraining && jobStatus?.status === 'failed' && (
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl space-y-2">
                  <div className="flex items-center gap-2 text-rose-800 font-bold text-sm">
                    <WarningCircle size={18} weight="bold" className="text-rose-600" />
                    Training Run Failed
                  </div>
                  <p className="text-xs text-rose-700 font-mono bg-white p-2.5 rounded-xl border border-rose-100">
                    {jobStatus.error || 'An unexpected error occurred during model fitting.'}
                  </p>
                  <Button
                    onClick={() => {
                      setJobStatus(null);
                      setIsTraining(false);
                    }}
                    variant="outline"
                    className="text-xs py-1.5 mt-2"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </>
          ) : (
            /* MODEL REGISTRY TAB */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-outfit font-extrabold text-base text-slate-900">
                    Model Registry
                  </h3>
                  <p className="text-[11px] font-semibold text-slate-400">
                    Registered model versions with explicit activation and rollback.
                  </p>
                </div>
                <Button
                  onClick={loadRegisteredModels}
                  variant="outline"
                  className="py-1 px-2.5 text-[10px] flex items-center gap-1"
                >
                  <ArrowClockwise size={12} weight="bold" />
                  Refresh
                </Button>
              </div>

              {loadingModels ? (
                <div className="py-12 text-center text-slate-400 text-xs font-bold flex items-center justify-center gap-2">
                  <Spinner size={16} className="animate-spin text-accent" />
                  Loading model registry...
                </div>
              ) : (
                <div className="space-y-3">
                  {models.map((mod) => {
                    const isActive = activeModelVersion === mod.version_id;
                    const isCandidate = !isActive && (mod.status === 'candidate' || mod.version_id === jobStatus?.version_id);

                    return (
                      <div
                        key={mod.version_id}
                        className={`p-4 rounded-2xl border transition-all ${
                          isActive
                            ? 'bg-accent/5 border-accent/40 shadow-xs'
                            : 'bg-white border-slate-200/80 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-outfit font-extrabold text-sm text-slate-900 font-mono">
                                {mod.version_id}
                              </span>

                              {/* Consistent Status Badges: ACTIVE / CHAMPION, CANDIDATE, INACTIVE / PREVIOUS */}
                              {isActive ? (
                                <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                  ACTIVE / CHAMPION
                                </span>
                              ) : isCandidate ? (
                                <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                                  CANDIDATE
                                </span>
                              ) : (
                                <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                                  INACTIVE / PREVIOUS
                                </span>
                              )}
                            </div>

                            <div className="text-[11px] text-slate-500 font-medium space-y-0.5">
                              {mod.is_baseline ? (
                                <>
                                  <div>Training: <span className="text-slate-700 font-semibold">Frozen Baseline</span></div>
                                  <div>Range: <span className="font-mono text-slate-700 font-semibold">{formatDisplayDate('2005-01-29')} → {formatDisplayDate('2024-02-13')}</span></div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    Parent: <span className="font-mono text-slate-700 font-semibold">{mod.parent_version || 'v3_baseline'}</span>
                                  </div>
                                  <div>
                                    Training: <span className="text-slate-700 font-semibold">{mod.training_type || 'Champion → Warm-Start Candidate'}</span>
                                  </div>
                                  {mod.training_range ? (
                                    <div>
                                      Range: <span className="font-mono text-slate-700 font-semibold">{formatDisplayDate(mod.training_range.from_date)} → {formatDisplayDate(mod.training_range.to_date)}</span>
                                    </div>
                                  ) : mod.from_date && mod.to_date && mod.from_date !== 'N/A' ? (
                                    <div>
                                      Range: <span className="font-mono text-slate-700 font-semibold">{formatDisplayDate(mod.from_date)} → {formatDisplayDate(mod.to_date)}</span>
                                    </div>
                                  ) : null}
                                </>
                              )}
                              {mod.created_at && (
                                <div>
                                  Created: <span className="text-slate-700 font-semibold">{new Date(mod.created_at).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            {isActive ? (
                              <span className="text-[11px] font-bold text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
                                <Check size={13} weight="bold" /> In Use
                              </span>
                            ) : (
                              <Button
                                onClick={() => setConfirmActivateVersion(mod.version_id)}
                                variant={mod.is_baseline ? 'outline' : isCandidate ? 'accent' : 'outline'}
                                className="py-1 px-3 text-[10px] font-bold"
                              >
                                {mod.is_baseline ? 'Rollback to v3' : isCandidate ? 'Activate' : 'Activate Version'}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Model Metrics Summary if available */}
                        {mod.metrics && (
                          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-[10px] text-slate-600 font-mono">
                            <div>
                              <span className="text-slate-400 font-sans font-extrabold uppercase text-[9px] block">Existing (Head A)</span>
                              NDCG@10: <span className="font-bold text-slate-800">
                                {mod.metrics.candidate?.head_a?.ndcg_10 ?? mod.metrics.head_a?.ndcg_10 ?? '—'}
                              </span>
                              {' | '}
                              Hit@10: <span className="font-bold text-slate-800">
                                {formatMetric(mod.metrics.candidate?.head_a?.hit_10 ?? mod.metrics.head_a?.hit_10, true)}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 font-sans font-extrabold uppercase text-[9px] block">Prospects (Head B)</span>
                              NDCG@10: <span className="font-bold text-slate-800">
                                {mod.metrics.candidate?.head_b?.ndcg_10 ?? mod.metrics.head_b?.ndcg_10 ?? '—'}
                              </span>
                              {' | '}
                              Hit@10: <span className="font-bold text-slate-800">
                                {formatMetric(mod.metrics.candidate?.head_b?.hit_10 ?? mod.metrics.head_b?.hit_10, true)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[11px] text-slate-400 font-semibold flex items-center gap-1.5">
            <ShieldCheck size={14} weight="bold" className="text-emerald-500" />
            Active Champion: <span className="font-mono font-bold text-slate-700">{activeModelVersion}</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={onClose}
              variant="outline"
              className="py-2 px-3.5 text-xs"
            >
              {isTraining ? 'Minimize' : 'Close'}
            </Button>
            {activeTab === 'train' && !isTraining && !jobStatus?.version_id && (
              <Button
                onClick={startTrainingJob}
                disabled={precheckLoading || !precheckData?.can_train}
                className="bg-accent hover:bg-accent-hover text-white py-2 px-4 text-xs font-bold"
              >
                <Sparkle size={14} weight="bold" />
                Start Warm-Start Training
              </Button>
            )}
          </div>
        </div>

        {/* ACTIVATION CONFIRMATION MODAL DIALOG */}
        {confirmActivateVersion && (
          <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-xs animate-in fade-in duration-150">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200 shrink-0">
                  <Warning size={20} weight="bold" />
                </div>
                <div>
                  <h3 className="font-outfit font-extrabold text-base text-slate-900 leading-tight">
                    Activate {confirmActivateVersion} as the active production model?
                  </h3>
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-slate-600 leading-relaxed font-medium">
                <div>
                  Current active model: <span className="font-mono font-bold text-slate-800">{activeModelVersion}</span>
                </div>
                <div className="text-slate-500">
                  Your current model can be rolled back later.
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <Button
                  onClick={() => setConfirmActivateVersion(null)}
                  variant="outline"
                  disabled={isActivating}
                  className="text-xs py-2 px-3.5 font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmActivate}
                  disabled={isActivating}
                  className="bg-accent hover:bg-accent-hover text-white text-xs py-2 px-4 font-bold"
                >
                  {isActivating ? (
                    <span className="flex items-center gap-1.5">
                      <Spinner size={13} className="animate-spin" />
                      Activating...
                    </span>
                  ) : (
                    'Activate'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
